"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  title: "Application-specific feasibility testing"
}, {
  id: 2,
  title: "Full qualification & lifecycle support"
}, {
  id: 3,
  title: "Regulated compliance assurance (cGMP/GLP)"
}];
export default function OptimizeAnalysis() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="optimizeanalysiscta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="relative rounded-2xl bg-sidebar text-sidebar-foreground border border-sidebar-border p-8 md:p-12 lg:p-16 shadow-xl overflow-hidden">
          {/* Subtle decorative background glow */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-sidebar-accent/50 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Main Content Column */}
            <div className="lg:col-span-8 space-y-6">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sidebar-accent text-sidebar-foreground text-xs font-medium">
                <Sparkles className="size-3.5 text-primary" />
                <span>Expert Analytical Advisory</span>
              </motion.div>

              <motion.h2 variants={itemVariants} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                Ready to Optimize Your Laboratory Analysis?
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base sm:text-lg text-sidebar-foreground/85 max-w-2xl leading-relaxed">
                Partner with our application specialists to evaluate your methods,
                streamline raw material identification, or integrate in-line PAT
                monitoring across Ireland and the UK.
              </motion.p>

              {/* Value check items */}
              <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                {features.map((feature, i) => <div key={feature.id} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm text-sidebar-foreground/90 font-medium">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{feature.title}</span>
                  </div>)}
              </motion.div>
            </div>

            {/* Action Buttons Column */}
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col items-stretch justify-center gap-4">
              <motion.div variants={itemVariants} className="w-full">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base py-6 shadow-md transition-all group">
                  <Link to="/DiscussYourApplicationPage" className="flex items-center justify-center gap-2">
                    <span>Discuss Your Application</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div variants={itemVariants} className="w-full">
                <Button asChild variant="outline" size="lg" className="w-full border-sidebar-border bg-sidebar-accent text-white hover:bg-sidebar-accent/80 font-medium text-base py-6 shadow-xs">
                  <Link to="/BookADemoPage" className="flex items-center justify-center gap-2">
                    <FlaskConical className="size-4 text-primary" />
                    <span>Book a Demo</span>
                  </Link>
                </Button>
              </motion.div>

              <motion.p variants={itemVariants} className="text-center text-xs text-sidebar-foreground/70">
                {"Fast turnaround • Consultation with dedicated application engineers"}
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}